import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileUp, MapPin, RefreshCw, Save, Sparkles } from 'lucide-react';
import { SURVEY_STATUSES, areaFromFileName, formatDateTime, formatDay, parsePrefixes, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from '../bookings/util.js';
import SurveyForm from './SurveyForm.jsx';
import AreaOverview from './AreaOverview.jsx';
import AssignSurveyors from './AssignSurveyors.jsx';
import AutoAssign from './AutoAssign.jsx';
import { nearestText, nearestTo, useNearby } from './nearby.js';
import { STATUS_CLASS, assignSurveyor, dismiss, setGang, setJobStatus, useSurveys } from './store.js';

// Office: Operations > Network Plus surveys. Network Plus sends a sheet per
// area of jobs to survey before they're booked. Import it here; each job goes
// to the gang whose postcodes cover it; supervisors survey on the app; the
// answers come through here live; Export gives their sheet back, filled in.
// ?job=<id> opens one job.
const TABS = [['jobs', 'Jobs'], ['gangs', 'Gangs and areas'], ['imports', 'Import a sheet']];

export default function Surveys({ me }) {
  const [params, setParams] = useBoardParams();
  const tab = params.get('tab') || 'jobs';
  const canEdit = me.capabilities.includes('page.surveys.edit');
  const s = useSurveys();
  const go = (t) => setParams(t === 'jobs' ? {} : { tab: t });

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Network Plus surveys</h1>
            <p className="muted">Pre-site surveys from Network Plus’s sheets. Each job goes to the gang whose postcodes cover it; supervisors survey on the app and the answers show here as they come in. Export gives Network Plus their sheet back with columns 7–14 filled in.</p>
          </div>
        </div>
        <div className="ticket-views">
          {TABS.filter(([id]) => canEdit || id === 'jobs').map(([id, label]) => (
            <button key={id} type="button" className={`chip${tab === id ? ' on' : ''}`} onClick={() => go(id)}>{label}</button>
          ))}
          {s.data?.areas?.length > 0 && <ExportMenu kind="xlsx" label="Export Excel" areas={s.data.areas} total={s.jobs.length} />}
          {s.data?.areas?.length > 0 && <ExportMenu kind="pdf" label="Export PDF" areas={s.data.areas} total={s.jobs.length} />}
          <button type="button" className="btn icon-only" aria-label="Refresh" onClick={s.reload}><RefreshCw size={18} aria-hidden="true" /></button>
        </div>
        {s.refused.map((m) => (
          <p key={m.id} className="notice-line notice-bad" role="alert">
            {m.message || 'A change wasn’t accepted.'} <button type="button" className="link-btn" onClick={() => dismiss(m)}>OK</button>
          </p>
        ))}
      </section>
      {tab === 'jobs' && <Jobs me={me} s={s} canEdit={canEdit} />}
      {tab === 'gangs' && canEdit && <Gangs s={s} />}
      {tab === 'imports' && canEdit && <Imports s={s} onDone={() => { s.reload(); go('jobs'); }} />}
    </div>
  );
}

// The board's links keep ?board=1 while it was asked for, so on a phone
// closing a job or changing tab stays on the board (see pick.jsx).
function useBoardParams() {
  const [params, setParams] = useSearchParams();
  const board = params.get('board') === '1';
  return [params, (p) => setParams(board ? { ...p, board: '1' } : p)];
}

async function download(area, batch = null, kind = 'xlsx') {
  const q = new URLSearchParams({ ...(area ? { area } : {}), ...(batch ? { batch } : {}) });
  const blob = await api(`/surveys/export.${kind}?${q}`, { as: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const named = `${area ? `Nano ${area}` : 'Nano surveys'}${batch ? ` batch ${batch}` : ''}`;
  a.download = `${named} ${formatDay(new Date().toISOString().slice(0, 10)).replace(/\//g, '-')}.${kind}`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

// One job's survey (answers and photos) as a PDF.
async function downloadSurvey(job) {
  const blob = await api(`/surveys/jobs/${job.id}/survey.pdf`, { as: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `Survey ${job.jobNumber}.pdf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

// The board's columns; the office can drag them into any order (kept on
// this computer). Under the address of a job still to survey: the nearest
// engineer and team right now (their van on Samsara, straight line).
const COLUMNS = [
  { id: 'batch', label: 'Batch', cell: (j) => (j.batchId ? <>#{j.batchId}<div className="cell-sub">{formatDay(j.receivedOn)}</div></> : '') },
  { id: 'job', label: 'Job Number', cell: (j) => j.jobNumber },
  { id: 'address', label: 'Address', cell: (j, nearby) => <><strong className="cell-main">{j.address}</strong><NearestLine job={j} nearby={nearby} /></> },
  { id: 'postcode', label: 'Postcode', cell: (j) => j.postcode },
  { id: 'status', label: 'Job Status', cell: (j) => <span className={`status ${STATUS_CLASS[j.jobStatus] || ''}`}>{j.sending ? 'Sending…' : j.jobStatus}</span> },
  { id: 'presite', label: 'Presite Status', cell: (j) => j.presiteStatus || '' },
  { id: 'gang', label: 'Gang', cell: (j) => j.gangName || <span className="cell-none">None</span> },
  { id: 'surveyor', label: 'Surveyor', cell: (j) => j.surveyorName || <span className="cell-none">Unassigned</span> },
  { id: 'planned', label: 'Planned', cell: (j) => formatDay(j.plannedDate) },
  { id: 'surveyed', label: 'Surveyed', cell: (j) => (j.surveyedAt ? <>{formatDateTime(j.surveyedAt)}{j.surveyedByName && <div className="cell-sub">{j.surveyedByName}{j.photos ? ` · ${j.photos} photo${j.photos === 1 ? '' : 's'}` : ''}</div>}</> : '') },
];
const COLS_KEY = 'nu.surveys.columns';

// A gang in the dropdowns: number, team and who's in it today.
const gangLabel = (g) => `${g.gangName ? `${g.gangName} (${g.teamName})` : g.teamName}${g.members ? `: ${g.members}` : ''}`;

function NearestLine({ job, nearby }) {
  const n = job.jobStatus === 'To Presite' ? nearestTo(job, nearby) : null;
  return n ? <div className="cell-sub survey-near"><MapPin size={12} aria-hidden="true" /> Nearest: {nearestText(n)}</div> : null;
}

function useColumns() {
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_KEY));
      if (Array.isArray(saved)) return [...saved.filter((id) => COLUMNS.some((c) => c.id === id)), ...COLUMNS.map((c) => c.id).filter((id) => !saved.includes(id))];
    } catch { /* default order */ }
    return COLUMNS.map((c) => c.id);
  });
  const [dragCol, setDragCol] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const moveCol = (from, to) => {
    setDragCol(null);
    setOverCol(null);
    if (!from || !to || from === to) return;
    setOrder((o) => {
      const n = o.filter((id) => id !== from);
      n.splice(n.indexOf(to) + (o.indexOf(from) < o.indexOf(to) ? 1 : 0), 0, from);
      try { localStorage.setItem(COLS_KEY, JSON.stringify(n)); } catch { /* this visit only */ }
      return n;
    });
  };
  return { cols: order.map((id) => COLUMNS.find((c) => c.id === id)), dragCol, overCol, setDragCol, setOverCol, moveCol };
}

function Jobs({ me, s, canEdit }) {
  const { cols, dragCol, overCol, setDragCol, setOverCol, moveCol } = useColumns();
  const [params, setParams] = useBoardParams();
  const { data, jobs } = s;
  const [f, setF] = useState({ area: '', status: '', team: '', surveyor: '', batch: '', q: '' });
  const [picked, setPicked] = useState(() => new Set());
  const [bulk, setBulk] = useState({ team: '' });
  const [assigning, setAssigning] = useState(null); // job ids
  const [auto, setAuto] = useState(false);
  const [done, setDone] = useState(null);
  const vans = useNearby();
  const nearby = vans.data;
  const [error, setError] = useState(null);
  const openId = params.get('job');
  const open = jobs.find((j) => String(j.id) === openId) || null;

  const list = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    return jobs.filter((j) => (!f.area || j.areaCode === f.area)
      && (!f.status || j.jobStatus === f.status)
      && (!f.team || (f.team === 'none' ? !j.teamId : String(j.teamId) === f.team))
      && (!f.surveyor || (f.surveyor === 'none' ? !j.surveyorIds?.length : j.surveyorIds?.includes(f.surveyor)))
      && (!f.batch || String(j.batchId) === f.batch)
      && (!q || `${j.jobNumber} ${j.address} ${j.postcode} ${j.floc || ''}`.toLowerCase().includes(q)));
  }, [jobs, f]);

  if (!data) return <section className="card"><p className="muted">{s.status === 'error' ? 'Couldn’t load the surveys.' : 'Loading…'}</p></section>;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const all = list.length > 0 && list.every((j) => picked.has(j.id));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const ids = [...picked].filter((id) => list.some((j) => j.id === id));
  const applyGang = async () => {
    await setGang(ids, bulk.team === 'none' ? null : Number(bulk.team));
    setPicked(new Set());
  };
  const gangsOn = data.gangs.filter((g) => g.gangName);

  return (
    <>
      {!data.areas.length && <section className="card"><p className="muted">No sheets imported yet.{canEdit ? ' Use Import a sheet.' : ''}</p></section>}
      {data.areas.length > 0 && (
        <AreaOverview areas={data.areas} jobs={jobs} onExport={(code, kind) => download(code, null, kind).catch((e) => setError(problem(e)))} onPick={(j) => setParams({ job: j.id })} />
      )}
      {error && <p className="notice-line notice-bad">{error}</p>}
      {done && <p className="notice-line notice-ok" role="status">{done}</p>}

      <section className="card">
        <div className="filters">
          <input className="search" type="search" placeholder="Search job number, address, postcode, Floc" value={f.q} onChange={set('q')} aria-label="Search" />
          <select value={f.area} onChange={set('area')} aria-label="Area">
            <option value="">All areas</option>
            {data.areas.map((a) => <option key={a.code} value={a.code}>{a.name}</option>)}
          </select>
          <select value={f.status} onChange={set('status')} aria-label="Job Status">
            <option value="">Any status</option>
            {SURVEY_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
          <select value={f.team} onChange={set('team')} aria-label="Gang">
            <option value="">Any gang</option>
            <option value="none">No gang</option>
            {data.gangs.map((g) => <option key={g.teamId} value={g.teamId}>{gangLabel(g)}</option>)}
          </select>
          <select value={f.batch} onChange={set('batch')} aria-label="Batch">
            <option value="">Any batch</option>
            {(data.batches || []).map((b) => <option key={b.id} value={b.id}>{batchLabel(b)}</option>)}
          </select>
          <select value={f.surveyor} onChange={set('surveyor')} aria-label="Surveyor">
            <option value="">Any surveyor</option>
            <option value="none">Unassigned</option>
            {data.surveyors.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {canEdit && ids.length > 0 && (
          <div className="survey-bulk">
            <strong>{ids.length} selected</strong>
            <button type="button" className="btn primary" onClick={() => setAssigning(ids)}>Assign surveyors…</button>
            <span>
              <select value={bulk.team} onChange={(e) => setBulk((b) => ({ ...b, team: e.target.value }))} aria-label="Gang for the selected jobs">
                <option value="">Gang…</option>
                <option value="none">No gang</option>
                {gangsOn.map((g) => <option key={g.teamId} value={g.teamId}>{gangLabel(g)}</option>)}
              </select>
              <button type="button" className="btn" disabled={!bulk.team} onClick={applyGang}>Set gang</button>
            </span>
            <button type="button" className="link-btn" onClick={() => setPicked(new Set())}>Clear</button>
          </div>
        )}
        {(canEdit || nearby?.located) && (
          <div className="survey-tools">
            {canEdit && (
              <button type="button" className="btn primary with-icon" onClick={() => { setDone(null); setAuto(true); }}>
                <Sparkles size={16} aria-hidden="true" /> Auto-assign{ids.length ? ` from the ${ids.length} ticked` : ''}
              </button>
            )}
            {nearby?.located && (
              <button type="button" className="btn primary with-icon" disabled={vans.loading} onClick={vans.refresh}>
                <RefreshCw size={16} aria-hidden="true" className={vans.loading ? 'spin' : ''} /> {vans.loading ? 'Refreshing…' : 'Refresh locations'}
              </button>
            )}
            {nearby?.at && <span className="small muted">Van locations {formatDateTime(nearby.at).slice(-5)}</span>}
          </div>
        )}
        <p className="small muted">
          {list.length} of {jobs.length} jobs
          {f.batch && <> · <button type="button" className="link-btn with-icon" onClick={() => download(null, f.batch).catch((e) => setError(problem(e)))}><Download size={14} aria-hidden="true" /> Export batch {f.batch}</button></>}
        </p>
        <div className="table-scroll">
          <table className="table clickable survey-table">
            <thead>
              <tr>
                {canEdit && <th><input type="checkbox" checked={all} aria-label="Select all shown" onChange={() => setPicked(all ? new Set() : new Set(list.map((j) => j.id)))} /></th>}
                {cols.map((c) => (
                  <th key={c.id} draggable onDragStart={(e) => { setDragCol(c.id); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragOver={(e) => { e.preventDefault(); if (dragCol && dragCol !== c.id) setOverCol(c.id); }}
                    onDragLeave={() => setOverCol(null)} onDrop={(e) => { e.preventDefault(); moveCol(dragCol, c.id); }}
                    onDragEnd={() => { setDragCol(null); setOverCol(null); }}
                    className={`drag-col${dragCol === c.id ? ' dragging' : ''}${overCol === c.id ? ' drop-here' : ''}`} title="Drag to move this column">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((j) => (
                <tr key={j.id} tabIndex={0} onClick={() => setParams({ job: j.id })} onKeyDown={(e) => { if (e.key === 'Enter') setParams({ job: j.id }); }}>
                  {canEdit && <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={picked.has(j.id)} onChange={() => toggle(j.id)} aria-label={`Select ${j.jobNumber}`} /></td>}
                  {cols.map((c) => <td key={c.id}>{c.cell(j, nearby)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal open={Boolean(open)} wide title={open ? `${open.jobNumber}: ${open.address}, ${open.postcode}` : ''} onClose={() => setParams({})}>
        {open && <JobDetail me={me} job={open} data={data} canEdit={canEdit} onAssign={setAssigning} nearby={nearby} />}
      </Modal>
      <AutoAssign
        open={auto}
        jobIds={ids.length ? ids : list.map((j) => j.id)}
        fromTicked={ids.length > 0}
        nearby={nearby}
        onClose={() => setAuto(false)}
        onAssign={async (jobIds, people) => {
          setAuto(false);
          await assignSurveyor(jobIds, people);
          setPicked(new Set());
          setDone(`${jobIds.length} survey${jobIds.length === 1 ? '' : 's'} assigned. They show in the team’s app.`);
        }}
      />
      <AssignSurveyors
        open={Boolean(assigning)}
        jobs={jobs.filter((j) => assigning?.includes(j.id))}
        allJobs={jobs}
        onClose={() => setAssigning(null)}
        onAssign={async (people) => {
          const forIds = assigning;
          setAssigning(null);
          await assignSurveyor(forIds, people);
          if (forIds.length > 1) setPicked(new Set());
        }}
      />
    </>
  );
}

function JobDetail({ me, job, data, canEdit, onAssign, nearby }) {
  const [detail, setDetail] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  useEffect(() => {
    api(`/surveys/jobs/${job.id}`).then(setDetail, () => {});
  }, [job.id, job.updatedAt, job.surveyedAt]);
  const gangsOn = data.gangs.filter((g) => g.gangName);

  return (
    <div className="stack">
      <dl className="survey-facts small">
        <div><dt>Area</dt><dd>{job.areaName}</dd></div>
        {job.batchId && <div><dt>Batch</dt><dd>#{job.batchId}, received {formatDay(job.receivedOn)}{job.lastBatchId && job.lastBatchId !== job.batchId ? ` (last in #${job.lastBatchId})` : ''}</dd></div>}
        {job.floc && <div><dt>Floc</dt><dd>{job.floc}</dd></div>}
        <div>
          <dt>Job Status</dt>
          <dd>
            {canEdit ? (
              <select value={job.jobStatus} onChange={(e) => setJobStatus(job, e.target.value)} aria-label="Job Status">
                {SURVEY_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            ) : <span className={`status ${STATUS_CLASS[job.jobStatus]}`}>{job.jobStatus}</span>}
          </dd>
        </div>
        <div>
          <dt>Gang</dt>
          <dd>
            {canEdit ? (
              <select value={job.teamId || ''} onChange={(e) => setGang([job.id], e.target.value ? Number(e.target.value) : null)} aria-label="Gang">
                <option value="">No gang</option>
                {gangsOn.map((g) => <option key={g.teamId} value={g.teamId}>{gangLabel(g)}</option>)}
              </select>
            ) : (job.gangName || 'None')}
            {job.teamSetByHand && <span className="small muted"> set by hand</span>}
          </dd>
        </div>
        <div>
          <dt>Surveyor</dt>
          <dd>
            {job.surveyorName || 'Unassigned'}
            {canEdit && <> <button type="button" className="link-btn" onClick={() => onAssign([job.id])}>{job.surveyorIds?.length ? 'Change' : 'Assign'}</button></>}
            <NearestLine job={job} nearby={nearby} />
          </dd>
        </div>
        {job.surveyWhere && (
          <div><dt>Surveyed at</dt><dd><a href={`https://www.google.com/maps?q=${job.surveyWhere.lat},${job.surveyWhere.lon}`} target="_blank" rel="noreferrer">Phone’s location</a>{job.surveyWhere.accuracy ? ` (±${job.surveyWhere.accuracy} m)` : ''}</dd></div>
        )}
        {(job.npGardStatus || job.npPlannedDate || job.npFropt || job.npComments) && (
          <div><dt>Network Plus</dt><dd>{[job.npGardStatus && `GARD ${job.npGardStatus}`, job.npPlannedDate && `planned ${job.npPlannedDate}`, job.npFropt, job.npComments].filter(Boolean).join(' · ')}</dd></div>
        )}
      </dl>
      <div className="survey-head">
        <h3>Survey</h3>
        {job.surveyedAt && (
          <button type="button" className="btn with-icon" onClick={() => { setPdfError(null); downloadSurvey(job).catch((e) => setPdfError(problem(e))); }}>
            <Download size={16} aria-hidden="true" /> Survey PDF
          </button>
        )}
      </div>
      {pdfError && <p className="notice-line notice-bad">{pdfError}</p>}
      {job.surveyStartedAt && <p className="small muted">Started {formatDateTime(job.surveyStartedAt)}{job.startWhere ? <> at <a href={`https://www.google.com/maps?q=${job.startWhere.lat},${job.startWhere.lon}`} target="_blank" rel="noreferrer">the phone’s location</a></> : ''}.</p>}
      <SurveyForm me={me} job={job} canEdit={canEdit} gangs={data.gangs} photosOn={data.photos !== false} />
      {detail?.history?.length > 0 && (
        <div>
          <h3>History</h3>
          <ul className="survey-history small">
            {detail.history.map((h, i) => <li key={i}><span className="muted">{formatDateTime(h.at)}</span> {h.summary}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// Each team's gang number (column 13) and the postcodes it covers. Saving
// re-matches every job that wasn't given a gang by hand.
function Gangs({ s }) {
  const { data, jobs } = s;
  const [edits, setEdits] = useState({});
  const [msg, setMsg] = useState(null);
  if (!data) return <section className="card"><p className="muted">Loading…</p></section>;
  const val = (g, k) => edits[g.teamId]?.[k] ?? (k === 'prefixes' ? g.prefixes.join(', ') : g.gangName || '');
  const set = (g, k, v) => setEdits((e) => ({ ...e, [g.teamId]: { gangName: val(g, 'gangName'), prefixes: val(g, 'prefixes'), ...e[g.teamId], [k]: v } }));
  const save = async (g) => {
    setMsg(null);
    try {
      const r = await api(`/surveys/gangs/${g.teamId}`, { method: 'PUT', body: { gangName: val(g, 'gangName'), prefixes: val(g, 'prefixes') } });
      setEdits((e) => { const n = { ...e }; delete n[g.teamId]; return n; });
      setMsg({ tone: 'ok', text: `${g.teamName} saved${r.rematched ? `; ${r.rematched} job${r.rematched === 1 ? '' : 's'} moved to the right gang` : ''}.` });
      s.reload();
    } catch (e) {
      setMsg({ tone: 'bad', text: problem(e) });
    }
  };
  const open = jobs.filter((j) => j.jobStatus !== 'Completed');
  const noGang = open.filter((j) => !j.teamId);

  return (
    <section className="card">
      <p className="small">Give each team its five-digit Network Plus gang number (it goes in the sheet’s Gang Name column) and the postcodes it covers: whole areas (<code>LE</code>), districts (<code>LE13</code>) or sectors (<code>LE13 1</code>), separated by commas. The closest fit wins, so <code>LE13</code> beats <code>LE</code>.</p>
      {noGang.length > 0 && <p className="notice-line notice-warn">{noGang.length} open job{noGang.length === 1 ? ' has' : 's have'} no gang: {[...new Set(noGang.map((j) => j.postcode.split(' ')[0]))].sort().join(', ')}.</p>}
      {msg && <p className={`notice-line notice-${msg.tone}`} role="status">{msg.text}</p>}
      <div className="table-scroll">
        <table className="table">
          <thead><tr><th>Team</th><th>Gang number</th><th>Postcodes covered</th><th className="num">Open jobs</th><th /></tr></thead>
          <tbody>
            {data.gangs.map((g) => {
              const typed = val(g, 'prefixes');
              const tidy = parsePrefixes(typed);
              return (
                <tr key={g.teamId} className={g.active ? '' : 'inactive'}>
                  <td><strong>{g.teamName}</strong>{!g.active && <div className="small muted">Closed</div>}</td>
                  <td><input className="cell-input" value={val(g, 'gangName')} inputMode="numeric" maxLength={5} onChange={(e) => set(g, 'gangName', e.target.value.replace(/\D/g, ''))} aria-label={`${g.teamName} gang number`} placeholder="5 digits" /></td>
                  <td>
                    <input className="cell-input wide" value={typed} onChange={(e) => set(g, 'prefixes', e.target.value)} aria-label={`${g.teamName} postcodes`} placeholder="e.g. LE13, LE14" />
                    {typed && tidy.join(', ') !== typed.toUpperCase().replace(/\s*,\s*/g, ', ').trim() && <div className="small muted">Reads as: {tidy.join(', ') || 'nothing'}</div>}
                  </td>
                  <td className="num">{open.filter((j) => j.teamId === g.teamId).length}</td>
                  <td><button type="button" className="btn with-icon" disabled={!edits[g.teamId] || (val(g, 'gangName') !== '' && val(g, 'gangName').length !== 5)} onClick={() => save(g)}><Save size={16} aria-hidden="true" /> Save</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// A batch as it reads in a list: #3 · Leicester · 30/10/2026.
const batchLabel = (b) => `#${b.id} · ${b.areaName} · ${formatDay(b.receivedOn)}`;

// Upload a sheet: check it first (what's new, what changes), then import.
function Imports({ s, onDone }) {
  const [file, setFile] = useState(null);
  const [area, setArea] = useState({ code: '', name: '' });
  const [received, setReceived] = useState(todayUk());
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [past, setPast] = useState([]);
  useEffect(() => { api('/surveys/imports').then(setPast, () => {}); }, []);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setCheck(null);
    setError(null);
    const known = s.data?.areas || [];
    const guess = areaFromFileName(f.name);
    const match = known.find((a) => a.code === guess.code);
    setArea({ code: guess.code, name: match?.name || guess.name });
  };
  const send = async (dryRun) => {
    setBusy(true);
    setError(null);
    try {
      const q = new URLSearchParams({ area: area.code, areaName: area.name, fileName: file.name, receivedOn: received, ...(dryRun ? { dryRun: '1' } : {}) });
      const r = await api(`/surveys/imports?${q}`, { method: 'POST', raw: file });
      if (dryRun) setCheck(r);
      else onDone();
    } catch (e) {
      setError(problem(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="card stack">
        <p className="small">Upload the sheet exactly as Network Plus sent it (.xlsx with a <strong>Data</strong> tab). Each sheet becomes a numbered batch with the date it was received. Jobs are matched on Job Number: new ones join the batch, ones we already have are updated with Network Plus’s columns (they keep the batch they first came in on), and our survey answers are never overwritten.</p>
        <label className="btn with-icon file-btn">
          <FileUp size={18} aria-hidden="true" /> {file ? 'Choose another sheet' : 'Choose sheet'}
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={pick} />
        </label>
        {file && (
          <>
            <p><strong>{file.name}</strong></p>
            <div className="fld-row">
              <label className="fld narrow"><span>Area code</span><input value={area.code} maxLength={4} onChange={(e) => { setArea((a) => ({ ...a, code: e.target.value.toUpperCase() })); setCheck(null); }} /></label>
              <label className="fld"><span>Area name</span><input value={area.name} maxLength={60} onChange={(e) => { setArea((a) => ({ ...a, name: e.target.value })); setCheck(null); }} /></label>
              <label className="fld narrow"><span>Received from Network Plus</span><input type="date" value={received} max={todayUk()} onChange={(e) => setReceived(e.target.value)} /></label>
            </div>
            <div className="actions">
              <button type="button" className="btn" disabled={busy || !area.code || !area.name} onClick={() => send(true)}>{busy && !check ? 'Checking…' : 'Check sheet'}</button>
              {check && <button type="button" className="btn primary" disabled={busy} onClick={() => send(false)}>{busy ? 'Importing…' : `Import ${check.total} jobs`}</button>}
            </div>
          </>
        )}
        {check && (
          <ul className="small">
            <li><strong>{check.added}</strong> new, <strong>{check.updated}</strong> updated, {check.unchanged} unchanged</li>
            {check.noGang > 0 && <li className="warn-text">{check.noGang} new job{check.noGang === 1 ? '' : 's'} with no gang covering the postcode (set up Gangs and areas, before or after)</li>}
            {check.noLocation > 0 && <li className="warn-text">{check.noLocation} postcode{check.noLocation === 1 ? '' : 's'} not found, so not on the distance sort</li>}
            {check.otherArea > 0 && <li className="warn-text">{check.otherArea} job{check.otherArea === 1 ? ' is' : 's are'} already in another area; they stay there</li>}
            {check.problems.map((p) => <li key={p} className="warn-text">{p}</li>)}
          </ul>
        )}
        {error && <p className="notice-line notice-bad">{error}</p>}
      </section>
      {past.length > 0 && (
        <section className="card">
          <h2>Batches</h2>
          <table className="table">
            <thead><tr><th>Batch</th><th>Received</th><th>Imported</th><th>Sheet</th><th>Area</th><th className="num">Jobs</th><th className="num">New</th><th className="num">Updated</th><th>By</th><th /></tr></thead>
            <tbody>
              {past.map((p) => (
                <tr key={p.id}>
                  <td><strong>#{p.id}</strong></td><td>{formatDay(p.receivedOn)}</td>
                  <td className="small">{formatDateTime(p.importedAt)}</td><td>{p.fileName}</td><td>{p.areaName}</td>
                  <td className="num">{p.total}</td><td className="num">{p.added}</td><td className="num">{p.updated}</td><td className="small">{p.importedByName}</td>
                  <td>{p.added > 0 && <button type="button" className="btn with-icon" onClick={() => download(null, p.id).catch(() => {})}><Download size={14} aria-hidden="true" /> Export</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}


// "Export Excel ▾" / "Export PDF ▾": one area's, or Both (all areas).
function ExportMenu({ kind, label, areas, total }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!e.target.closest?.('.export-menu')) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('click', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const go = (area) => { setOpen(false); download(area, null, kind).catch((e) => setError(problem(e))); };
  return (
    <span className="export-menu">
      <button type="button" className="btn with-icon" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Download size={16} aria-hidden="true" /> {label} ▾
      </button>
      {open && (
        <span className="export-list" role="menu">
          {areas.map((a) => (
            <button key={a.code} type="button" role="menuitem" onClick={() => go(a.code)}>{a.name}<span>{a.total} jobs</span></button>
          ))}
          <hr />
          <button type="button" role="menuitem" onClick={() => go(null)}>Both (all areas)<span>{total} jobs</span></button>
        </span>
      )}
      {error && <span className="small warn-text">{error}</span>}
    </span>
  );
}
